import {
  BULK_EXCLUSION_LABELS,
  type BulkEmailPreviewResponse,
  type BulkEmailSendResponse,
  type BulkExclusionCode,
  type EmailTemplateDTO,
  type LeadSelection,
} from '@hearth/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight, FilePlus, Mail, Send, Users } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, Field, Input, Select, Spinner, Textarea, useConfirm, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { ApiError, api, errorMessage } from '../../lib/api';
import { formatDateTime, formatNumber, pluralize } from '../../lib/format';
import { DeliveryWarning } from '../email/DeliveryWarning';
import { SaveAsTemplateDialog } from '../email/SaveAsTemplateDialog';

type HandOff = { selection: LeadSelection; count: number; description: string };

/**
 * Bulk email is two deliberate steps: compose, then a review that shows exactly who will and will not receive
 * it and why. The confirmation is bound to what was reviewed — if anything changes, the server asks for a
 * fresh review instead of sending something the salesperson did not see.
 */
export function BulkEmailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { can } = useAuth();
  const handOff = location.state as HandOff | null;
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [name, setName] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [sample, setSample] = useState(0);
  const [openReason, setOpenReason] = useState<BulkExclusionCode | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const { data: templates } = useQuery({
    queryKey: ['templates', 'active'],
    queryFn: ({ signal }) => api.get<{ items: EmailTemplateDTO[] }>('/api/templates?active=true', { signal }),
  });

  const content = {
    selection: handOff?.selection,
    templateId: templateId || null,
    subject: useDebouncedValue(subject, 400),
    body: useDebouncedValue(body, 400),
    scheduledFor: scheduledFor ? new Date(scheduledFor).toISOString() : null,
  };
  const { data: preview, isFetching, refetch } = useQuery({
    queryKey: ['bulk-email', 'preview', content],
    queryFn: ({ signal }) => api.post<BulkEmailPreviewResponse>('/api/bulk-email/preview', content, { signal }),
    enabled: Boolean(handOff) && content.subject.trim() !== '' && content.body.trim() !== '',
  });

  const send = useMutation({
    mutationFn: (typedConfirmation?: string) =>
      api.post<BulkEmailSendResponse>('/api/bulk-email/send', {
        ...content,
        subject,
        body,
        name: name.trim() || subject,
        confirmationToken: preview!.confirmationToken,
        selectionDescription: handOff?.description ?? null,
        typedConfirmation,
      }),
    onSuccess: (result) => {
      toast.success(result.created ? `Queued ${result.queued} emails` : 'This send was already submitted');
      navigate(`/email/campaigns/${result.campaignId}`);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        toast.error('Something changed since the review — the recipient list was refreshed. Check it and confirm again.');
        void refetch();
        return;
      }
      toast.error(errorMessage(error));
    },
  });

  if (!handOff) {
    return (
      <Page width="narrow">
        <PageHeader title="Bulk email" />
        <Card>
          <EmptyState
            icon={Users}
            title="Pick recipients first"
            description="Select pizzerias in the lead table (or all results of a filter), then choose “Send email”."
            action={
              <Link to="/leads">
                <Button variant="primary">Go to leads</Button>
              </Link>
            }
          />
        </Card>
      </Page>
    );
  }

  const chooseTemplate = (id: string) => {
    setTemplateId(id);
    const template = (templates?.items ?? []).find((item) => item.id === id);
    if (!template) return;
    setSubject(template.subject);
    setBody(template.body);
    if (!name) setName(template.name);
  };
  const sourceTemplate = (templates?.items ?? []).find((item) => item.id === templateId) ?? null;
  // The history name is a good template name once it says more than the template it started from.
  const templateName =
    name.trim() !== '' && name.trim() !== sourceTemplate?.name ? name.trim() : sourceTemplate ? `${sourceTemplate.name} (my version)` : subject.trim();

  const blocking = [...(preview?.complianceIssues ?? []), ...(preview?.templateIssues ?? [])];
  const canSend = preview && preview.recipientCount > 0 && blocking.length === 0 && !isFetching;
  const current = preview?.samples[sample];

  const confirmAndSend = async () => {
    if (!preview) return;
    const ok = await confirm({
      title: `Send to ${pluralize(preview.recipientCount, 'recipient')}?`,
      message: (
        <>
          <p>{pluralize(preview.excludedCount, 'selected lead')} will be skipped. Emails go out within your sending limits and window.</p>
          {preview.deliveryWarning && <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-900">{preview.deliveryWarning}</p>}
        </>
      ),
      confirmLabel: scheduledFor ? 'Schedule' : 'Send',
      requireText: preview.requiresTypedConfirmation ? (preview.confirmationText ?? undefined) : undefined,
    });
    if (ok) send.mutate(preview.requiresTypedConfirmation ? (preview.confirmationText ?? undefined) : undefined);
  };

  return (
    <Page width="wide">
      <PageHeader
        title="Bulk email"
        description={handOff.description}
        breadcrumbs={[{ label: 'Leads', to: '/leads' }, { label: 'Bulk email' }]}
      />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <Card>
          <CardHeader
            title="1. Compose"
            description="Variables are filled per recipient; add fallbacks like {{first_name|there}}."
            actions={
              can('templates:manage') ? (
                <Button size="sm" variant="ghost" icon={FilePlus} disabled={subject.trim() === '' || body.trim() === ''} onClick={() => setSavingTemplate(true)}>
                  Save as template
                </Button>
              ) : undefined
            }
          />
          <CardBody className="space-y-3">
            <Field label="Template">
              <Select value={templateId} onChange={(event) => chooseTemplate(event.target.value)} placeholder="Write from scratch">
                {(templates?.items ?? []).map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Subject">
              <Input value={subject} onChange={(event) => setSubject(event.target.value)} />
            </Field>
            <Field label="Message">
              <Textarea rows={12} value={body} onChange={(event) => setBody(event.target.value)} />
            </Field>
            <Field label="Name (for your history)">
              <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Autumn oven check-in — Montréal" />
            </Field>
            <Field label="Send later" hint="Empty = as soon as the sending window allows.">
              <Input type="datetime-local" value={scheduledFor} onChange={(event) => setScheduledFor(event.target.value)} />
            </Field>
          </CardBody>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader title="2. Review" description="Nothing is sent until you confirm." actions={isFetching ? <Spinner className="size-4" /> : undefined} />
            <CardBody>
              {!preview ? (
                <p className="text-[13px] text-slate-500">Write a subject and message (or pick a template) to see who will receive it.</p>
              ) : (
                <div className="space-y-4 text-[13px]">
                  {preview.deliveryWarning && <DeliveryWarning message={preview.deliveryWarning} />}

                  <div className="grid grid-cols-3 gap-3 text-center">
                    <Stat label="Selected leads" value={preview.selectedLeads} />
                    <Stat label="Recipients" value={preview.recipientCount} strong />
                    <Stat label="Skipped" value={preview.excludedCount} />
                  </div>

                  {blocking.length > 0 && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-red-800">
                      <p className="flex items-center gap-1 font-medium">
                        <AlertTriangle className="size-4" /> Cannot send yet
                      </p>
                      <ul className="mt-1 list-disc pl-5">
                        {blocking.map((issue) => (
                          <li key={issue}>{issue}</li>
                        ))}
                      </ul>
                      {preview.complianceIssues.length > 0 &&
                        (can('settings:manage') ? (
                          <Link to="/settings/compliance" className="mt-1 inline-block underline">
                            Open compliance settings
                          </Link>
                        ) : (
                          <p className="mt-1">Ask an administrator to fill in these settings (Settings → Email compliance).</p>
                        ))}
                    </div>
                  )}

                  {Object.keys(preview.countsByReason).length > 0 && (
                    <div>
                      <p className="mb-1 font-medium text-slate-800">Why some leads are skipped</p>
                      <ul className="space-y-1">
                        {(Object.entries(preview.countsByReason) as [BulkExclusionCode, number][]).map(([code, total]) => (
                          <li key={code}>
                            <button type="button" className="flex w-full items-center justify-between rounded px-2 py-1 hover:bg-slate-50" onClick={() => setOpenReason(openReason === code ? null : code)}>
                              <span>{BULK_EXCLUSION_LABELS[code]}</span>
                              <Badge color="slate" size="sm">
                                {total}
                              </Badge>
                            </button>
                            {openReason === code && (
                              <ul className="mb-2 ml-3 space-y-0.5 text-xs text-slate-600">
                                {preview.excluded
                                  .filter((item) => item.code === code)
                                  .slice(0, 50)
                                  .map((item) => (
                                    <li key={`${item.businessId}:${item.email ?? ''}`}>
                                      {item.businessName}
                                      {item.email ? ` — ${item.email}` : ''}: {item.reason}
                                    </li>
                                  ))}
                              </ul>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {preview.missingVariables.length > 0 && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-900">
                      {preview.missingVariables.map((variable) => (
                        <p key={variable.variable}>
                          {`{{${variable.variable}}}`} is empty for {pluralize(variable.count, 'recipient')}
                          {variable.fallback !== null ? ` — “${variable.fallback}” will be used` : ' — it will render blank'}.
                        </p>
                      ))}
                    </div>
                  )}

                  {preview.estimate && (
                    <p className="text-slate-600">
                      Delivery: starts {formatDateTime(preview.estimate.startsAt)}, finishes around {formatDateTime(preview.estimate.finishesAt)} (
                      {preview.estimate.perMinuteLimit}/minute, {preview.estimate.dailyLimit}/day
                      {preview.estimate.window.enabled ? `, ${preview.estimate.window.startHour}:00–${preview.estimate.window.endHour}:00 weekdays` : ''}).
                    </p>
                  )}
                </div>
              )}
            </CardBody>
          </Card>

          {current && (
            <Card>
              <CardHeader
                title={`Sample ${sample + 1} of ${preview!.samples.length}`}
                description={`${current.businessName} — ${current.toEmail}`}
                actions={
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" icon={ChevronLeft} disabled={sample === 0} onClick={() => setSample(sample - 1)}>
                      Prev
                    </Button>
                    <Button size="sm" variant="ghost" iconRight={ChevronRight} disabled={sample >= preview!.samples.length - 1} onClick={() => setSample(sample + 1)}>
                      Next
                    </Button>
                  </div>
                }
              />
              <CardBody>
                <p className="mb-2 text-[13px] font-medium text-slate-900">{current.subject}</p>
                <pre className="max-h-80 overflow-auto whitespace-pre-wrap wrap-anywhere font-sans text-[13px] text-slate-700">{current.text}</pre>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardBody className="flex items-center justify-between gap-3">
              <p className="text-[13px] text-slate-600">
                {preview ? `${pluralize(preview.recipientCount, 'email')} will be queued.` : 'Review the send first.'}
              </p>
              <Button variant="primary" icon={scheduledFor ? Mail : Send} disabled={!canSend} loading={send.isPending} onClick={() => void confirmAndSend()}>
                {scheduledFor ? 'Schedule send' : 'Send'}
              </Button>
            </CardBody>
          </Card>
        </div>
      </div>
      {savingTemplate && (
        <SaveAsTemplateDialog
          subject={subject}
          body={body}
          defaultName={templateName.slice(0, 200)}
          defaultCategory={sourceTemplate?.category ?? 'custom'}
          onClose={() => setSavingTemplate(false)}
          onSaved={(template) => {
            setTemplateId(template.id);
            setSavingTemplate(false);
          }}
        />
      )}
    </Page>
  );
}

function Stat({ label, value, strong }: { label: string; value: number; strong?: boolean }) {
  return (
    <div className="rounded-md border border-slate-200 p-2">
      <p className={strong ? 'text-2xl font-semibold text-ember-700' : 'text-xl font-semibold text-slate-800'}>{formatNumber(value)}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}
